<template>
  <div class="book-list-container">
    <div v-if="books.length > 0" class="book-grid">
      <div v-for="book in books" :key="book.id" class="book-item fiction-book">
        <router-link :to="`/book/${book.slug}`">
          <div class="book-cover-container">
            <img :src="book.cover || placeholderImage" alt="Book Cover" class="book-thumbnail" />
            <div class="book-overlay">
              <h4 class="book-title-overlay">{{ book.title }}</h4>
              <p class="book-author-overlay">{{ book.author?.name || "Unknown" }}</p>
            </div>
          </div>
          <div class="book-info fiction-info">
            <div class="book-rating">
              <span v-for="star in 5" :key="star" class="star" :class="{'filled': star <= book.average_rating}">
                <font-awesome-icon :icon="['fas', 'star']" />
              </span>
            </div>
            <p class="book-meta">
              <font-awesome-icon :icon="['fas', 'bars']" /> {{ book.chapter_count }} bab |
              <font-awesome-icon :icon="['fas', 'eye']" /> {{ book.read_count }} |
              <font-awesome-icon :icon="['fas', 'clock']" /> <span v-html="formatReadTime(book.estimated_read_time)"></span>
            </p>
          </div>
        </router-link>
      </div>
    </div>

    <p v-if="books.length === 0 && loading" class="no-books">
      Buku sedang dimuat. Mohon tunggu.
      <font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" />
    </p>

    <p v-if="books.length === 0 && !loading" class="no-books">
      Tidak ada buku ditemukan untuk kategori ini.
    </p>

    <div ref="observerTarget" class="observer-target"></div>
  </div>
</template>

<script>
import axios from "axios";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import placeholderImage from '@/assets/placeholder.svg';

export default {
  name: "BookList",
  components: {
    FontAwesomeIcon,
  },
  props: {
    category: { 
      type: [String, Number], 
      default: null 
    },
    limit: { 
      type: Number, 
      default: 15 
    },
    ordering: { 
      type: String, 
      default: "-updated_date" 
    },
  },
  watch: {
    category: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          // Reset state dan fetch ulang buku
          this.books = [];
          this.page = 1;
          this.hasMoreBooks = true;
          this.fetchBooks(true);
        }
      },
      immediate: true
    }
  },
  data() {
    return {
      books: [],
      placeholderImage: placeholderImage,
      loading: true,
      page: 1,
      hasMoreBooks: true,
      observer: null,
    };
  },
  // Updated mounted method
mounted() {
    this.setupInfiniteScroll();
    // Explicitly call fetchBooks to ensure initial loading
    this.fetchBooks(true);
  },

  // Improved beforeUnmount method with better cleanup
  beforeUnmount() {
    if (this.observer) {
      // Make sure to unobserve the target before disconnecting
      if (this.$refs.observerTarget) {
        this.observer.unobserve(this.$refs.observerTarget);
      }
      this.observer.disconnect();
      this.observer = null;
    }
    
    // Clean up scroll event listener
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    setupInfiniteScroll() {
      // Clean up any existing observer first
      if (this.observer) {
        if (this.$refs.observerTarget) {
          this.observer.unobserve(this.$refs.observerTarget);
        }
        this.observer.disconnect();
        this.observer = null;
      }
      
      // Remove any existing scroll listener
      window.removeEventListener('scroll', this.handleScroll);
      
      if ('IntersectionObserver' in window) {
        try {
          this.observer = new IntersectionObserver(this.handleIntersect, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
          });

          if (this.$refs.observerTarget) {
            this.observer.observe(this.$refs.observerTarget);
          }
        } catch (error) {
          console.error('Failed to setup IntersectionObserver:', error);
          // Fallback to scroll event if IntersectionObserver fails
          window.addEventListener('scroll', this.handleScroll, { passive: true });
        }
      } else {
        // Fallback for browsers that don't support IntersectionObserver
        window.addEventListener('scroll', this.handleScroll, { passive: true });
      }
    },
    handleIntersect(entries) {
      if (entries[0].isIntersecting && !this.loading && this.hasMoreBooks) {
        this.loadMoreBooks();
      }
    },

    handleScroll() {
      const scrollPosition = window.innerHeight + window.pageYOffset;
      const pageBottom = document.documentElement.offsetHeight - 200;

      if (scrollPosition >= pageBottom && !this.loading && this.hasMoreBooks) {
        this.loadMoreBooks();
      }
    },

    async fetchBooks(isInitialLoad = false) {
      try {
        this.loading = true;

        // Reset page jika ini adalah load awal
        if (isInitialLoad) {
          this.page = 1;
          this.hasMoreBooks = true;
          this.books = []; // Reset books array pada load awal
        }

        let queryParams = `ordering=${this.ordering}&page=${this.page}`;

        if (this.limit) {
          queryParams += `&limit=${this.limit}`;
        }

        // Handle different category types
        if (this.category) {
          if (this.category === 'fiction') {
            queryParams += '&category_type=fiction';
          } else if (this.category === 'non-fiction') {
            queryParams += '&category_type=non-fiction';
          } else if (!isNaN(this.category)) {
            queryParams += `&category=${this.category}`;
          }
        }

        // Jika subCategory memiliki child, ambil semua buku di bawah subCategory
        if (this.category && this.$parent && this.$parent.filteredSubSubCategories && this.$parent.filteredSubSubCategories.length > 0) {
          queryParams += `&parent_category=${this.category}`;
        } else if (this.category && !isNaN(this.category)) {
          // Jika subCategory tidak memiliki child, ambil buku dari subCategory itu sendiri
          queryParams += `&category=${this.category}`;
        }

        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/books/?${queryParams}`);

        const newBooks = response.data.results || [];

        if (isInitialLoad) {
          // Create new array to ensure reactivity
          this.books = [...newBooks];
        } else {
          // Create new array by combining existing and new books
          this.books = [...this.books, ...newBooks];
        }

        // Update hasMoreBooks berdasarkan response
        this.hasMoreBooks = response.data.next !== null;

      } catch (err) {
        console.error("Failed to fetch books", err);
        if (isInitialLoad) {
          this.books = [];
        }
      } finally {
        this.loading = false;
      }
    },

    loadMoreBooks() {
      if (!this.loading && this.hasMoreBooks) {
        this.page += 1;
        this.fetchBooks(false);
      }
    },
    // Fungsi untuk memformat waktu baca
    formatReadTime(minutes) {
      if (!minutes) {
        return "0 menit";  // Atau nilai default lainnya jika estimated_read_time null/undefined
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      let formattedTime = "";

      if (hours > 0) {
        formattedTime += `${hours} jam `;
      }
      if (remainingMinutes > 0) {
        formattedTime += `${remainingMinutes} menit`;
      }
      if(hours == 0 && remainingMinutes == 0) {
          formattedTime = "Kurang dari 1 menit";
      }

      return formattedTime;
    },

  },
  async created() {
    // No need to call fetchBooks here as it will be triggered by watch.category
  },
};
</script>

<style scoped>
.book-list-container {
  padding: 20px;
  width: 100%;
}

.book-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 15px;
  justify-content: center;
}

.book-item {
  max-width: 250px;
  width: 100%;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  text-align: center;
  padding: 10px;
  margin: 0 auto;
}

.book-item:hover {
  transform: translateY(-5px);
}

.book-cover-container {
  position: relative;
  width: 100%;
  height: 280px;
  overflow: hidden;
  border-radius: 5px;
}

.book-thumbnail {
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-radius: 5px;
}

.fiction-book .book-thumbnail {
  height: 280px;
}

.book-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 15px 10px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  text-align: left;
}

.book-title-overlay {
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 5px 0;
  color: #fff;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);
  border-radius: 4px;
  display: inline-block;
}

.book-author-overlay {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
  margin: 0;
}

.book-info {
  padding: 10px;
}

.fiction-info {
  padding-top: 5px;
}

.book-title {
  font-size: 18px;
  font-weight: bold;
  margin: 5px 0;
}

.book-author {
  font-size: 13px;
  color: gray;
  font-style: italic;
  margin-bottom: 5px;
}

.book-rating {
  margin: 5px 0;
}

.star {
  color: #ccc;
  font-size: 18px;
}

.filled {
  color: gold;
}

.book-meta {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
}

.no-books, .loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 16px;
  color: #666;
  padding: 20px 0;
}

.observer-target {
  height: 20px;
  width: 100%;
  margin-top: 10px;
}

@media (min-width: 600px) {
  .book-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
}

@media (min-width: 900px) {
  .book-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }
}

@media (min-width: 1200px) {
  .book-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}
</style>
